import { Capacitor, registerPlugin } from '@capacitor/core'
import { useFavoritesStore } from '../store/favorites'
import { useStopsStore } from '../store/stops'
import { API_BASE_URL } from './env'

// The favorites the Android widget needs, pushed into native SharedPreferences. The widget
// is a native AppWidgetProvider with no WebView, so it can't read localStorage or Zustand —
// this bridge is its only source of which stops the user cares about, and of the API origin
// to fetch arrivals from.

export interface WidgetStopRef {
  id: string
  name: string
}

interface WidgetBridgePlugin {
  sync(data: {
    apiBaseUrl: string
    stops: WidgetStopRef[]
    busStops: WidgetStopRef[]
    favoriteRoutes: string[]
    favoriteBusRoutes: string[]
  }): Promise<void>
}

const WidgetBridge = registerPlugin<WidgetBridgePlugin>('WidgetBridge')

function buildPayload() {
  const fav = useFavoritesStore.getState()
  const stopNames = new Map(useStopsStore.getState().stops.map((s) => [s.id, s.name]))
  return {
    apiBaseUrl: API_BASE_URL,
    // Names may be unknown before the map loads station metadata. The native side keeps the
    // last known name for any id synced with an empty one, so never block on it.
    stops: [...fav.favorites].map((id) => ({ id, name: stopNames.get(id) ?? '' })),
    busStops: fav.favoriteBusStops.map((s) => ({ id: s.id, name: s.name })),
    // Subway favorites sort their rows to the top; both lists drive which lines the alerts
    // widget fetches.
    favoriteRoutes: [...fav.favoriteRoutes],
    favoriteBusRoutes: [...fav.favoriteBusRoutes],
  }
}

let syncTimer: ReturnType<typeof setTimeout> | null = null

/** Coalesce bursts (toggling several favorites, stops metadata arriving) into one sync. */
function scheduleSync() {
  if (syncTimer) clearTimeout(syncTimer)
  syncTimer = setTimeout(() => {
    syncTimer = null
    WidgetBridge.sync(buildPayload()).catch(() => {
      /* widget sync must never break the app */
    })
  }, 500)
}

/** Call once at startup (native builds only): pushes the current favorites to the widget
 *  store and keeps it in sync as favorites change or station names load. */
export function initWidgetBridge(): void {
  if (!Capacitor.isNativePlatform()) return

  useFavoritesStore.subscribe((state, prev) => {
    if (
      state.favorites !== prev.favorites ||
      state.favoriteBusStops !== prev.favoriteBusStops ||
      state.favoriteRoutes !== prev.favoriteRoutes ||
      state.favoriteBusRoutes !== prev.favoriteBusRoutes
    ) {
      scheduleSync()
    }
  })
  // Station names arrive with map data, usually after the first sync, so re-sync to give
  // empty-named favorites their real ones.
  useStopsStore.subscribe((state, prev) => {
    if (state.stops !== prev.stops) scheduleSync()
  })

  scheduleSync()
}
